import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';
import {
    Receipt, ChevronRight, RefreshCw, Download, Loader2, AlertTriangle, Filter, Search,
} from 'lucide-react';
import CustomDatePicker from '../../components/CustomDatePicker';

import {
    fetchTDSClients, fetchTDSSubClients, fetchTDSFinancialYears, fetchClientTDSReport,
    clearSubClients, clearClientTDSReport, resetClientTDSReport,
    selectCTRClientList, selectCTRClientListLoading,
    selectCTRSubClientList, selectCTRSubClientListLoading,
    selectCTRYearList, selectCTRYearListLoading,
    selectCTRReport, selectCTRReportLoading, selectCTRReportError,
} from '../../slices/ReportsSlice/clientTDSReportSlice';

const inputCls = 'w-full px-2.5 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg text-xs bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent';

const SEARCH_TYPES = [
    { searchval: 'ByYear', searchtext: 'Search By FY Year' },
    { searchval: 'ByDate', searchtext: 'Search By Date' },
];

const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const toDDMonYYYY = (val) => {
    if (!val) return '';
    const d = val instanceof Date ? val : new Date(val);
    if (isNaN(d.getTime())) return '';
    return `${String(d.getDate()).padStart(2, '0')}-${MONTH_ABBR[d.getMonth()]}-${d.getFullYear()}`;
};

const num = (v) => parseFloat(v || 0) || 0;
const money = (v) => num(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// spGetAllTDSclients' Clientname is "ClientName , ClientCode" (name first) for real rows,
// or the literal "SelectAll" for its synthetic first row — take the text before the comma.
const beforeComma = (raw) => (raw || '').split(',')[0].trim();
// spGetSubClientsbyClientForTDSReport's SubClientCodename is "SubClientCode ,Address"
// (code first) — take the text after the comma.
const afterComma = (raw) => {
    const i = (raw || '').indexOf(',');
    return i === -1 ? raw : raw.slice(i + 1).trim();
};

const ClientTDSReport = () => {
    const dispatch = useDispatch();

    const clientList = useSelector(selectCTRClientList);
    const clientListLoading = useSelector(selectCTRClientListLoading);
    const subClientListRaw = useSelector(selectCTRSubClientList);
    const subClientListLoading = useSelector(selectCTRSubClientListLoading);
    const yearList = useSelector(selectCTRYearList);
    const yearListLoading = useSelector(selectCTRYearListLoading);
    const report = useSelector(selectCTRReport);
    const reportLoading = useSelector(selectCTRReportLoading);
    const reportError = useSelector(selectCTRReportError);

    const [clientCode, setClientCode] = useState('');
    const [subClientCode, setSubClientCode] = useState('');
    const [searchBy, setSearchBy] = useState('');
    const [year, setYear] = useState('');
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [ranParams, setRanParams] = useState(null);

    const clientOptions = useMemo(() => clientList.map((c) => ({ ...c, Clientname: beforeComma(c.Clientname) })), [clientList]);
    const subClientOptions = useMemo(() => subClientListRaw.map((c) => ({ ...c, SubClientCodename: afterComma(c.SubClientCodename) })), [subClientListRaw]);

    useEffect(() => {
        dispatch(fetchTDSClients());
        dispatch(fetchTDSFinancialYears());
        return () => dispatch(resetClientTDSReport());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleClientChange = (val) => {
        setClientCode(val);
        setSubClientCode(''); setSearchBy(''); setYear(''); setFromDate(''); setToDate('');
        dispatch(clearSubClients()); dispatch(clearClientTDSReport());
        if (val) dispatch(fetchTDSSubClients(val));
    };

    const handleSubClientChange = (val) => {
        setSubClientCode(val);
        setSearchBy(''); setYear(''); setFromDate(''); setToDate('');
        dispatch(clearClientTDSReport());
    };

    const handleSearchByChange = (val) => {
        setSearchBy(val);
        setYear(''); setFromDate(''); setToDate('');
        dispatch(clearClientTDSReport());
    };

    const handleReset = () => {
        setClientCode(''); setSubClientCode(''); setSearchBy(''); setYear('');
        setFromDate(''); setToDate(''); setRanParams(null);
        dispatch(resetClientTDSReport());
        dispatch(fetchTDSClients());
        dispatch(fetchTDSFinancialYears());
    };

    const runReport = () => {
        if (!clientCode) { toast.warning('Select Client'); return; }
        if (!subClientCode) { toast.warning('Select SubClient'); return; }
        if (!searchBy) { toast.warning('Select Search Type'); return; }
        if (searchBy === 'ByYear' && !year) { toast.warning('Select Financial year'); return; }
        if (searchBy === 'ByDate') {
            if (toDate && !fromDate) { toast.warning('Select From Date'); return; }
            if (fromDate && !toDate) { toast.warning('Select To Date'); return; }
            if (!fromDate && !toDate) { toast.warning('Select From and To Date'); return; }
        }

        const params = {
            Clientcode: clientCode,
            SubClientcode: subClientCode,
            FromDate: searchBy === 'ByDate' ? toDDMonYYYY(fromDate) : '',
            ToDate: searchBy === 'ByDate' ? toDDMonYYYY(toDate) : '',
            SearchBy: searchBy,
            StartYear: searchBy === 'ByYear' ? year : '',
            EndYear: searchBy === 'ByYear' ? String(parseInt(year, 10) + 1) : '',
        };
        setRanParams(params);
        dispatch(fetchClientTDSReport(params));
    };

    const handleRefresh = () => { if (ranParams) dispatch(fetchClientTDSReport(ranParams)); };

    const totals = useMemo(() => report.reduce((acc, r) => {
        acc.InvoiceAmount += num(r.InvoiceAmount);
        acc.TDSAmount += num(r.TDSAmount);
        return acc;
    }, { InvoiceAmount: 0, TDSAmount: 0 }), [report]);

    const handleExport = () => {
        if (report.length === 0) { toast.warning('No data to export'); return; }
        const exportRows = report.map((r) => ({
            'Invoice Date': r.InvoiceDate, 'Invoice No': r.InvoiceNo,
            'Client Name and Code': r.ClientName, 'SubClient Name and Code': r.SubClientName,
            'Invoice Amount': r.InvoiceAmount, 'TDS Amount': r.TDSAmount,
        }));
        exportRows.push({ 'Invoice Date': '', 'Invoice No': '', 'Client Name and Code': 'TOTAL', 'SubClient Name and Code': '', 'Invoice Amount': totals.InvoiceAmount, 'TDS Amount': totals.TDSAmount });
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(exportRows), 'Client TDS');
        XLSX.writeFile(wb, `Client_TDS_Report_${new Date().toISOString().slice(0, 10)}.xlsx`);
        toast.success('Excel downloaded');
    };

    return (
        <div className="space-y-3 p-4 text-xs">
            {/* Header */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-3 border border-gray-200 dark:border-gray-700">
                <div className="flex items-start justify-between flex-wrap gap-3">
                    <div>
                        <h1 className="text-base font-bold text-gray-900 dark:text-white flex items-center gap-2">
                            <div className="p-1.5 bg-gradient-to-br from-indigo-500 to-violet-600 rounded-lg">
                                <Receipt className="h-4 w-4 text-white" />
                            </div>
                            Client TDS Report
                        </h1>
                        <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">TDS deducted against client invoices, by financial year or date range</p>
                        <nav className="flex items-center gap-1.5 text-[11px] text-gray-400 mt-1">
                            <span>Reports</span><ChevronRight className="w-3 h-3" /><span className="text-gray-700 dark:text-gray-200">Client TDS Report</span>
                        </nav>
                    </div>
                    <div className="flex items-center gap-2">
                        <button onClick={handleRefresh} disabled={reportLoading || !ranParams} className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xs font-medium transition-colors disabled:opacity-60">
                            <RefreshCw className={`h-3.5 w-3.5 ${reportLoading ? 'animate-spin' : ''}`} />Refresh
                        </button>
                        <button onClick={handleExport} disabled={report.length === 0} className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-lg text-xs font-medium transition-colors disabled:opacity-60">
                            <Download className="h-3.5 w-3.5" />Export Excel
                        </button>
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-3 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-1.5">
                        <Filter className="h-3.5 w-3.5 text-indigo-600" />
                        <h2 className="text-xs font-semibold text-gray-700 dark:text-gray-300">Filters</h2>
                    </div>
                    {ranParams && (
                        <button onClick={handleReset} className="flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-800 font-medium">
                            <RefreshCw className="w-3 h-3" /> Change Filters
                        </button>
                    )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
                    <div>
                        <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">Client</label>
                        <select className={inputCls} value={clientCode} onChange={(e) => handleClientChange(e.target.value)} disabled={clientListLoading || !!ranParams}>
                            <option value="">-Please Select-</option>
                            {clientOptions.map((c) => <option key={c.Clientcode} value={c.Clientcode}>{c.Clientname}</option>)}
                        </select>
                    </div>
                    {clientCode && (
                        <div>
                            <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">Sub-Client</label>
                            <select className={inputCls} value={subClientCode} onChange={(e) => handleSubClientChange(e.target.value)} disabled={subClientListLoading || !!ranParams}>
                                <option value="">-Please Select-</option>
                                <option value="SelectAll">Select All</option>
                                {subClientOptions.map((c) => <option key={c.SubClientCode} value={c.SubClientCode}>{c.SubClientCodename}</option>)}
                            </select>
                        </div>
                    )}
                    {subClientCode && (
                        <div>
                            <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">Search By</label>
                            <select className={inputCls} value={searchBy} onChange={(e) => handleSearchByChange(e.target.value)} disabled={!!ranParams}>
                                <option value="">-Please Select-</option>
                                {SEARCH_TYPES.map((s) => <option key={s.searchval} value={s.searchval}>{s.searchtext}</option>)}
                            </select>
                        </div>
                    )}
                    {searchBy === 'ByYear' && (
                        <div>
                            <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">Financial Year</label>
                            <select className={inputCls} value={year} onChange={(e) => setYear(e.target.value)} disabled={yearListLoading || !!ranParams}>
                                <option value="">-Please Select-</option>
                                {yearList.map((y) => <option key={y.StartYear} value={y.StartYear}>{y.YearValue}</option>)}
                            </select>
                        </div>
                    )}
                    {searchBy === 'ByDate' && (
                        <>
                            <div>
                                <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">From Date</label>
                                <CustomDatePicker value={fromDate} onChange={setFromDate} maxDate={new Date()} disabled={!!ranParams} />
                            </div>
                            <div>
                                <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">To Date</label>
                                <CustomDatePicker value={toDate} onChange={setToDate} minDate={fromDate || null} maxDate={new Date()} disabled={!!ranParams} />
                            </div>
                        </>
                    )}
                </div>
                {searchBy && (
                    <div className="flex gap-2 mt-3">
                        <button onClick={runReport} disabled={reportLoading || !!ranParams} className="px-4 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xs font-semibold transition-colors disabled:opacity-60 flex items-center gap-1.5">
                            <Search className="w-3.5 h-3.5" /> View
                        </button>
                        <button onClick={handleReset} className="px-4 py-1.5 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg text-xs font-semibold transition-colors">Reset</button>
                    </div>
                )}
            </div>

            {/* Report */}
            {reportLoading ? (
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 flex items-center justify-center gap-3 py-10">
                    <Loader2 className="h-6 w-6 animate-spin text-indigo-600" /><span className="text-xs text-gray-500 dark:text-gray-400">Loading report…</span>
                </div>
            ) : reportError ? (
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 flex items-center justify-center gap-3 py-10 text-red-500">
                    <AlertTriangle className="h-5 w-5" /><span className="text-xs">{reportError}</span>
                </div>
            ) : ranParams && report.length === 0 ? (
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 py-10 text-center text-xs text-gray-400">No Data Found.</div>
            ) : report.length > 0 ? (
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-700">
                        <h2 className="text-xs font-bold text-gray-700 dark:text-gray-300">Client TDS Details</h2>
                    </div>
                    <div className="overflow-auto max-h-[70vh]">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                            <thead className="bg-gradient-to-r from-indigo-600 to-violet-600 sticky top-0 z-10">
                                <tr>
                                    <th className="px-2 py-1.5 text-left text-[10px] font-bold text-white uppercase whitespace-nowrap">Invoice Date</th>
                                    <th className="px-2 py-1.5 text-left text-[10px] font-bold text-white uppercase whitespace-nowrap">Invoice No</th>
                                    <th className="px-2 py-1.5 text-left text-[10px] font-bold text-white uppercase whitespace-nowrap">Client Name and Code</th>
                                    <th className="px-2 py-1.5 text-left text-[10px] font-bold text-white uppercase whitespace-nowrap">SubClient Name and Code</th>
                                    <th className="px-2 py-1.5 text-right text-[10px] font-bold text-white uppercase whitespace-nowrap">Invoice Amount</th>
                                    <th className="px-2 py-1.5 text-right text-[10px] font-bold text-white uppercase whitespace-nowrap">TDS Amount</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                                {report.map((row, i) => (
                                    <tr key={row.RId ?? i} className="hover:bg-indigo-50 dark:hover:bg-indigo-900/10 transition-colors">
                                        <td className="px-2 py-1 text-xs whitespace-nowrap">{row.InvoiceDate}</td>
                                        <td className="px-2 py-1 text-xs whitespace-nowrap">{row.InvoiceNo}</td>
                                        <td className="px-2 py-1 text-xs">{row.ClientName}</td>
                                        <td className="px-2 py-1 text-xs">{row.SubClientName}</td>
                                        <td className="px-2 py-1 text-xs text-right font-semibold whitespace-nowrap">{money(row.InvoiceAmount)}</td>
                                        <td className="px-2 py-1 text-xs text-right font-semibold whitespace-nowrap">{money(row.TDSAmount)}</td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot>
                                <tr className="bg-gray-100 dark:bg-gray-900/50 font-bold">
                                    <td className="px-2 py-1 text-xs" colSpan={4}>TOTAL</td>
                                    <td className="px-2 py-1 text-xs text-right">{money(totals.InvoiceAmount)}</td>
                                    <td className="px-2 py-1 text-xs text-right">{money(totals.TDSAmount)}</td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </div>
            ) : null}
        </div>
    );
};

export default ClientTDSReport;
